import { useState, useEffect, useRef, useCallback } from "react";

export const useVariants = ({ authToken, switchDocument, setCredits }) => {
  const [variants, setVariants] = useState([]);
  const [activeVariantId, setActiveVariantId] = useState(null);
  const activeVariantIdRef = useRef(null);

  // Keep activeVariantIdRef in sync (fixes stale SSE closure)
  useEffect(() => {
    activeVariantIdRef.current = activeVariantId;
  }, [activeVariantId]);

  const fetchVariants = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch("/api/variants", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 403) {
        console.warn('[useVariants] 403: not authorized to fetch variants');
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setVariants(data.variants || []);
      }
    } catch {}
  }, [authToken]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  const handleSelectVariant = useCallback((variantId) => {
    if (variantId === null) {
      switchDocument({ type: 'master' });
    } else {
      switchDocument({ type: 'variant', id: variantId, document_data: null });
    }
  }, [switchDocument]);

  const handleCreateVariant = useCallback(async () => {
    try {
      const res = await fetch("/api/variants", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          name: `Variant — ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
          variant_type: "document",
          overrides: {
            name: "",
            email: "",
            phone: "",
            city: "",
            role: "",
            summary: "",
            skills: [],
            experience: [],
            education: [],
            certifications: [],
            extracurricular: [],
            achievements: [],
            links: {},
          }
        })
      });
      if (res.status === 403) {
        console.warn('[useVariants] 403: insufficient credits or unauthorized to create variant');
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setVariants((prev) => [...prev, data.variant]);
        handleSelectVariant(data.variant.id);
      }
    } catch (err) {
      console.error("Error creating variant:", err);
    }
  }, [authToken, handleSelectVariant]);

  const handleDeleteVariant = useCallback(async (variantId) => {
    if (!window.confirm("Delete this JD variant?")) return;
    try {
      const res = await fetch(`/api/variants/${variantId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        setVariants((prev) => prev.filter((v) => v.id !== variantId));
        if (activeVariantId === variantId) {
          setActiveVariantId(null);
        }
      }
    } catch {}
  }, [authToken, activeVariantId]);

  const handleVariantCreated = useCallback((newVariant, creditsRemaining) => {
    setVariants((prev) => [...prev, newVariant]);
    if (typeof creditsRemaining === "number" && setCredits) {
      setCredits(creditsRemaining);
    }
    handleSelectVariant(newVariant.id);
  }, [handleSelectVariant, setCredits]);

  return {
    variants,
    setVariants,
    activeVariantId,
    setActiveVariantId,
    activeVariantIdRef,
    fetchVariants,
    handleSelectVariant,
    handleCreateVariant,
    handleDeleteVariant,
    handleVariantCreated,
  };
};
